import AddIcon from "@mui/icons-material/Add";
import { Alert, AlertTitle, Box, Button, LinearProgress, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useSupabase, useSupabaseCallback } from "../../../server/supabase/hooks";
import type { UUID } from "../../../utils/uuid";
import {
  type AccountPersonField,
  SupabaseAccountPersonFieldService,
} from "../../event/server/supabase-account-person-field-service";
import {
  type InsightKeyFieldWithData,
  SupabaseInsightKeyFieldService,
  SupabaseInsightKeyFieldsViewService,
  getFieldPreviewValues,
  getInsightKeyFieldMetrics,
} from "../server/supabase-insight-key-field-service";
import { InsightKeyFieldList } from "./insight-key-field-list";
import { InsightKeyFieldPanel } from "./insight-key-field-panel";

const DEFAULTS = [{ field_key: "department", display_name: "Department" }] as const;

export const InsightKeyFieldsTab: FC = () => {
  const [fields, setFields] = useState<InsightKeyFieldWithData[] | undefined>();
  const [personFields, setPersonFields] = useState<AccountPersonField[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<"add" | "edit">("add");
  const [editingField, setEditingField] = useState<InsightKeyFieldWithData | null>(null);

  useSupabase(async ({ supabase, account_id }) => {
    const [viewResult, fieldsResult] = await Promise.all([
      new SupabaseInsightKeyFieldsViewService(supabase).getAll(account_id, {
        order: [{ column: "created_at" }],
      }),
      new SupabaseAccountPersonFieldService(supabase).getAll(account_id, {
        order: [{ column: "display_name" }],
      }),
    ]);

    if (viewResult.error || fieldsResult.error) {
      setLoadError(true);
      return;
    }

    setPersonFields(fieldsResult.data ?? []);

    if ((viewResult.data ?? []).length === 0) {
      const svc = new SupabaseInsightKeyFieldService(supabase);
      await Promise.all(DEFAULTS.map((d) => svc.insert({ ...d, account_id })));
      const reloaded = await new SupabaseInsightKeyFieldsViewService(supabase).getAll(account_id, {
        order: [{ column: "created_at" }],
      });
      if (reloaded.error) {
        setLoadError(true);
        return;
      }
      setFields(reloaded.data ?? []);
    } else {
      setFields(viewResult.data ?? []);
    }
  }, []);

  const handleSave = useSupabaseCallback(
    async (
      { supabase, account_id },
      mode: "add" | "edit",
      fieldKey: string,
      displayName: string,
      editingId?: UUID,
    ) => {
      if (mode === "add") {
        const { data, error } = await new SupabaseInsightKeyFieldService(supabase).insert({
          field_key: fieldKey,
          display_name: displayName,
          account_id,
        });
        if (error || !data) throw new Error("Insert failed");
        setFields((prev) => [...(prev ?? []), { ...data, has_data: false }]);
      } else if (editingId) {
        const { data, error } = await new SupabaseInsightKeyFieldService(supabase).update(
          editingId,
          { field_key: fieldKey, display_name: displayName },
        );
        if (error || !data) throw new Error("Update failed");
        setFields((prev) =>
          (prev ?? []).map((f) =>
            f.id === editingId ? { ...f, field_key: fieldKey, display_name: displayName } : f,
          ),
        );
      }
      setPanelOpen(false);
    },
    [],
  );

  const handleDelete = useSupabaseCallback(async ({ supabase }, id: UUID) => {
    setDeleteError(false);
    const error = await new SupabaseInsightKeyFieldService(supabase).delete(id);
    if (error) {
      setDeleteError(true);
      return;
    }
    setFields((prev) => (prev ?? []).filter((f) => f.id !== id));
  }, []);

  const loadPreview = useSupabaseCallback(async ({ supabase, account_id }, fieldKey: string) => {
    return getFieldPreviewValues(supabase, account_id, fieldKey);
  }, []);

  const loadMetrics = useSupabaseCallback(async ({ supabase, account_id }, fieldKey: string) => {
    return getInsightKeyFieldMetrics(supabase, account_id, fieldKey);
  }, []);

  if (loadError) {
    return (
      <Alert severity="error">
        <AlertTitle>Something went wrong</AlertTitle>
      </Alert>
    );
  }

  if (fields === undefined) return <LinearProgress />;

  const configuredKeys = fields.map((f) => f.field_key);

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Select fields from your HRIS/CSV file that our AI can use to generate insights on
        Communications Engagement and Survey results.
      </Typography>

      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to remove field. Please try again.
        </Alert>
      )}

      <Button
        startIcon={<AddIcon />}
        data-analytics-id="insight-key-fields-add-field"
        onClick={() => {
          setPanelMode("add");
          setEditingField(null);
          setPanelOpen(true);
        }}
        sx={{ mb: 2 }}
      >
        Add field
      </Button>

      <InsightKeyFieldList
        fields={fields}
        onEdit={(f) => {
          setEditingField(f);
          setPanelMode("edit");
          setPanelOpen(true);
        }}
        onDelete={(id) => void handleDelete(id)}
      />

      <InsightKeyFieldPanel
        key={`${String(panelOpen)}-${editingField?.id ?? "new"}`}
        open={panelOpen}
        mode={panelMode}
        field={editingField}
        personFields={personFields}
        configuredKeys={configuredKeys}
        onClose={() => setPanelOpen(false)}
        onSave={async (fieldKey, displayName) => {
          await handleSave(panelMode, fieldKey, displayName, editingField?.id);
        }}
        onLoadPreview={loadPreview}
        onLoadMetrics={loadMetrics}
      />
    </Box>
  );
};
